import React from 'react';

export type RecordHeaderProps = {
  title: React.ReactNode;
  className?: string;
  subline?: React.ReactNode;
  // Backward-compat alias used in some pages
  meta?: React.ReactNode;
  tabsInline?: React.ReactNode;
  mobileTabs?: React.ReactNode;
  // Optional full-width lifecycle/status rail rendered beneath the identity
  // and actions. It remains visible in the compact mobile header.
  progress?: React.ReactNode;
  sticky?: boolean;
  melted?: boolean;
  aside?: React.ReactNode;
  // Optional full-width footer row for record-level metrics and status.
  footer?: React.ReactNode | null;
  // Optional mobile-only compact menu content (e.g., Back/Next)
  mobileNav?: React.ReactNode;
  // Optional actions shown in the compact mobile strip. When omitted, the
  // strip continues to use mobileNav for backward compatibility.
  mobileActions?: React.ReactNode;
  // Optional mobile menu title override; falls back to title
  mobileTitle?: React.ReactNode;
  // Optional content beside the phone sheet's close button (a record's save status and help).
  menuHeaderExtras?: React.ReactNode;
  // Optional controlled state for the compact mobile menu. Pass both to drive
  // the sheet from outside — a page's melt bar keeps its own burger, which has
  // to open this same menu once the header itself has scrolled away. Omit both
  // and the component manages the menu itself, as it always has.
  menuOpen?: boolean;
  onMenuOpenChange?: (open: boolean) => void;
};

export function RecordHeader({ title, className, subline, meta, tabsInline, mobileTabs, progress, sticky, melted, aside, footer, mobileNav, mobileActions, mobileTitle, menuHeaderExtras, menuOpen, onMenuOpenChange }: RecordHeaderProps) {
  const effectiveSubline = subline ?? meta;
  const cls = [
    'record-header',
    className || '',
    sticky ? 'is-sticky' : '',
    melted ? 'has-melted' : ''
  ].filter(Boolean).join(' ');
  const [selfMenuOpen, setSelfMenuOpen] = React.useState(false);
  const menuIsControlled = menuOpen !== undefined;
  const isMenuOpen = menuIsControlled ? menuOpen : selfMenuOpen;
  const setMenuOpen = React.useCallback((next: boolean)=>{
    if(!menuIsControlled) setSelfMenuOpen(next);
    onMenuOpenChange?.(next);
  }, [menuIsControlled, onMenuOpenChange]);
  const closeMenu = React.useCallback(()=> setMenuOpen(false), [setMenuOpen]);
  React.useEffect(()=>{
    function onEsc(e: KeyboardEvent){ if(e.key==='Escape') closeMenu(); }
    if(isMenuOpen){ window.addEventListener('keydown', onEsc); } else { window.removeEventListener('keydown', onEsc as any); }
    return ()=> window.removeEventListener('keydown', onEsc as any);
  }, [isMenuOpen, closeMenu]);

  const handleMenuSelection = React.useCallback((event: React.MouseEvent<HTMLElement>)=>{
    const target = event.target as HTMLElement | null;
    if(!target) return;
    const interactive = target.closest('a, button, [role="menuitem"], [data-rh-menu-dismiss], [data-rh-menu-keep-open]');
    if(!(interactive instanceof HTMLElement)) return;
    if(!event.currentTarget.contains(interactive)) return;
    if(interactive.hasAttribute('data-rh-menu-keep-open')) return;
    if(interactive.getAttribute('data-rh-menu-dismiss') === 'false') return;
    closeMenu();
  }, [closeMenu]);

  const inlineTabsContent = React.useMemo(()=>{
    if(!tabsInline) return null;
    if(React.isValidElement(tabsInline)){
      return React.cloneElement(tabsInline, { key: 'inline-tabs' });
    }
    return <React.Fragment key="inline-tabs">{tabsInline}</React.Fragment>;
  }, [tabsInline]);

  const mobileTabsContent = React.useMemo(()=>{
    const source = mobileTabs ?? tabsInline;
    if(!source) return null;
    if(React.isValidElement(source)){
      const existingClass = (source.props as { className?: string }).className || '';
      const merged = [existingClass, 'rh-menu-tabs-list'].filter(Boolean).join(' ');
      return React.cloneElement(source, { key: 'mobile-tabs', className: merged });
    }
    return (
      <div className="rh-menu-tabs-list" key="mobile-tabs">
        {source}
      </div>
    );
  }, [mobileTabs, tabsInline]);

  const inlineMobileActions = mobileActions ?? mobileNav;
  const showMobileBar = Boolean(inlineMobileActions || mobileNav || mobileTitle || title);

  return (
    <div className={cls}>
      {/* Mobile hamburger trigger (hidden on desktop via CSS) */}
      {showMobileBar ? (
        <div className="record-header-mobile">
          <button
            type="button"
            className="rh-burger"
            aria-label={isMenuOpen? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            onClick={()=> setMenuOpen(!isMenuOpen)}
          >
            <span className="rh-burger-lines" aria-hidden="true" />
          </button>
          {/* Optional inline title preview on mobile bar (kept minimal) */}
          <div className="rh-mobile-title" title={typeof title==='string'? title: undefined}>
            {mobileTitle ?? title}
          </div>
          {inlineMobileActions ? (
            <div className="rh-mobile-actions" role="toolbar" aria-label="Record navigation">
              {inlineMobileActions}
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="record-header-main">
        {title}
        {effectiveSubline ? (
          <p className="record-subline text-sm fg-muted">{effectiveSubline}</p>
        ) : null}
        {inlineTabsContent ? (
          <div className="record-tabs-inline" role="tablist">
            {inlineTabsContent}
          </div>
        ) : null}
      </div>
      <div className="record-actions">
        {aside}
      </div>
      {footer ? (
        <div className="record-header__footer">
          {footer}
        </div>
      ) : null}
      {progress ? (
        <div className="record-header__progress">
          {progress}
        </div>
      ) : null}

      {/* Mobile dropdown menu content */}
      {(isMenuOpen && (mobileNav || mobileTitle || mobileTabsContent)) ? (
        <div className="rh-menu" role="dialog" aria-modal="true">
          <div className="rh-menu-inner">
            <div className="rh-menu-header">
              <div className="rh-menu-title">{mobileTitle ?? title}</div>
              {menuHeaderExtras ? <div className="rh-menu-header-extras">{menuHeaderExtras}</div> : null}
              <button type="button" className="btn xs subtle rh-menu-close" onClick={closeMenu} aria-label="Close">×</button>
            </div>
            <div className="rh-menu-body">
              {mobileTabsContent ? (
                <div className="rh-menu-tabs" onClick={handleMenuSelection}>
                  {mobileTabsContent}
                </div>
              ) : null}
              {mobileNav ? (
                <div className="rh-menu-nav" onClick={handleMenuSelection}>
                  {mobileNav}
                </div>
              ) : null}
            </div>
          </div>
          <button type="button" className="rh-menu-scrim" aria-label="Close menu" onClick={closeMenu} />
        </div>
      ) : null}
    </div>
  );
}

export default RecordHeader;
